<template>
  <div>
    <PageTitle title="Edit Warehouse" />
    <div
      v-if="item"
      class="home-content"
    >
      <v-form
        v-model="valid"
        @keydown.native.enter="validate"
      >
        <v-row style="max-width: 1000px">
          <v-col
            sm="12"
            md="6"
            lg="6"
            cols="12"
          >
            <div class="header-content">
              Info
            </div>
            <div class="mb-4">
              <span class="label-text">Title *</span>
              <div class="input-mb input-box">
                <v-text-field
                  v-model="item.name"
                  dense
                  placeholder="Enter warehouse title"
                  :rules="nameRules"
                  maxlength="128"
                  class="r-field item-name-field"
                />
              </div>
            </div>
            <div class="mb-4">
              <span class="label-text">Notes</span>
              <div class="input-mb input-box d-flex flex-nowrap">
                <v-textarea
                  v-model="item.description"
                  dense
                  placeholder="Enter description"
                  maxlength="128"
                  class="r-field item-name-field"
                />
              </div>
            </div>
          </v-col>
          <v-col cols="1" />
          <v-col
            sm="12"
            md="5"
            lg="5"
            cols="12"
          >
            <div class="header-content">
              General Settings
            </div>
            <div class="mb-4">
              <span class="label-text">Currencies *</span>
              <div class="input-mb input-box">
                <v-select
                  v-model="currentCurrency"
                  dense
                  :items="currencies"
                  item-text="title"
                  item-value="value"
                  required
                  class="general-settings-select"
                  @change="changeCurrency"
                />
              </div>
            </div>
            <div class="mb-4">
              <v-checkbox
                v-model="item.active"
                label="Switch to this warehouse"
                color="#8074c2"
              />
            </div>
          </v-col>
        </v-row>

        <v-row style="max-width: 1400px">
          <v-col>
            <v-tabs>
              <v-tab href="#map">
                Locations
              </v-tab>
              <v-tab-item
                value="map"
                :transition="false"
              >
                <v-row class="pt-8">
                  <v-col cols="12">
                    <div class="header-content">
                      Warehouse Map
                    </div>
                    <WarehouseMap :warehouse="item" />
                  </v-col>
                </v-row>
              </v-tab-item>

              <v-tab href="#users">
                Users
              </v-tab>
              <v-tab-item
                value="users"
                :transition="false"
              >
                <v-row class="pt-8">
                  <v-col cols="12">
                    <div class="header-content">
                      Users with access to the warehouse
                    </div>
                    <div class="table-wrapper mt-6">
                      <v-row class="table-controls mb-1 items-center">
                        <v-col cols="12">
                          <v-menu
                            v-model="addingUserMenuState"
                            class="select-warehouse-body"
                            min-width="220"
                            transition="slide-y-transition"
                            :close-on-content-click="false"
                            offset-y
                            bottom
                            right
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                depressed
                                v-bind="attrs"
                                color="#8074c2"
                                class="px-4 text-white"
                                v-on="on"
                              >
                                Add users
                                <v-icon>mdi-chevron-down</v-icon>
                              </v-btn>
                            </template>
                            <div class="bg-white pb-4">
                              <v-col class="py-0 px-4">
                                <v-text-field
                                  v-model="searchUsersInput"
                                  placeholder="Search..."
                                  hide-details
                                  autocomplete="off"
                                  class="mt-0 pt-2"
                                />
                              </v-col>
                              <div class="to-selecting-list mt-2">
                                <v-list
                                  v-if="filtredUsersList.length"
                                  max-height="150"
                                >
                                  <v-list-item
                                    v-for="(item, i) in filtredUsersList"
                                    :key="i"
                                    :class="{'v-list-item--active': item.active}"
                                    style="min-height: 32px"
                                  >
                                    <v-list-item-title>
                                      <div
                                        class="flex items-center"
                                      >
                                        <v-simple-checkbox
                                          v-model="item.selected"
                                          color="#8074c2"
                                          @input="handleChangeSelectedUsersList(item)"
                                        />
                                        <span class="ml-1 warehouse-list-title">{{ item.firstName }} {{ item.lastName }}</span>
                                      </div>
                                    </v-list-item-title>
                                  </v-list-item>
                                </v-list>
                                <p
                                  v-else
                                  class="px-2 py-1 text-center font-italic"
                                >
                                  No Users Found
                                </p>
                              </div>
                            </div>
                          </v-menu>
                        </v-col>
                      </v-row>
                      <v-divider class="row" />
                      <v-row class="justify-space-between">
                        <v-col
                          cols="12"
                          class="px-0"
                        >
                          <v-data-table
                            :headers="usersHeaders"
                            :items="item.info.users"
                            :page.sync="usersPage"
                            :items-per-page="usersPerPage"
                            item-key="id"
                            hide-default-footer
                            @page-count="usersPageCount = $event"
                          >
                            <template :slot='"no-results"'>
                              <p class="text-h6 my-16">No Users found</p>
                            </template>
                            <template :slot='"no-data"'>
                              <p class="text-h6 my-16">No Users found</p>
                            </template>
                            <template v-slot:[`item.selected`]>
                              <v-simple-checkbox
                                color="#7972c0"
                                :ripple="false"
                              />
                            </template>
                            <template v-slot:[`item.createdAt`]="{ item }">
                              {{ item.createdAt | formatDate(getDateTimeFormat) }}
                            </template>
                            <template v-slot:[`item.lastVisit`]="{ item }">
                              {{ item.lastVisit | formatDate(getDateTimeFormat) }}
                            </template>
                            <template v-slot:[`item.inviteStatus`]="{ item }">
                              <span
                                v-if="item.inviteStatus === 'accepted'"
                                style="
                                  background-color: #d6f7e2;
                                  color: #3d654d;
                                  border-radius: 4px;
                                "
                                class="pa-1"
                              >
                                {{ item.inviteStatus }}
                              </span>
                              <span
                                v-else
                                style="
                                  background-color: #fff1d6;
                                  color: #b67501;
                                  border-radius: 4px;
                                "
                                class="pa-1"
                              >
                                {{ item.inviteStatus }}
                              </span>
                            </template>
                            <template v-slot:[`item.actions`]="{ item }">
                              <v-row class="flex-nowrap">
                                <v-col cols="6" class="px-1" v-if='$getRight("userManagement") && item.inviteStatus !== "owner"'>
                                  <router-link class="ml-1" :to='{name: "EditUser", params: {id: item.id}}'>
                                    <img
                                      style="height: 16px; width: 16px"
                                      src="@/assets/icons/svg/draw.svg"
                                      title="Edit"
                                    >
                                  </router-link>
                                </v-col>
                                <v-col
                                  cols="6"
                                  class="px-1"
                                >
                                  <img
                                    v-if="$getRight('warehouseEdit') && item.inviteStatus !== 'owner'"
                                    style="height: 16px; width: 16px"
                                    src="@/assets/icons/svg/delete1.svg"
                                    class="cursor-point"
                                    title="Delete"
                                    @click="deleteUser(item.id)"
                                  >
                                </v-col>
                              </v-row>
                            </template>
                          </v-data-table>
                        </v-col>
                      </v-row>
                      <v-row class="pagination-wrapper">
                        <v-col cols="5">
                          <span class="text-nowrap mr-2">Items per page</span>
                          <v-menu offset-y>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                text
                                outlined
                                v-bind="attrs"
                                style="padding: 0; font-weight: 600"
                                v-on="on"
                              >
                                {{ usersPerPage }}
                                <v-icon>mdi-menu-down</v-icon>
                              </v-btn>
                            </template>
                            <v-list>
                              <v-list-item
                                v-for="(item, index) in [5, 10, 20, 25, 50]"
                                :key="index"
                                link
                                @click="usersPerPage = item"
                              >
                                <v-list-item-title>{{ item }}</v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </v-col>
                        <v-col
                          cols="7"
                          class="d-flex justify-end"
                        >
                          <v-pagination
                            v-model="usersPage"
                            :length="usersPageCount"
                            :total-visible="6"
                            color="#7972C0"
                          />
                        </v-col>
                      </v-row>
                    </div>
                  </v-col>
                </v-row>
              </v-tab-item>

              <v-tab href="#sync">
                Synchronization
              </v-tab>
              <v-tab-item
                value="sync"
                :transition="false"
              >
                <v-row class="pt-8">
                  <v-col cols="12">
                    <div class="header-content">
                      Synchronization
                    </div>
                    <div v-if="!item.integration">
                      <p>No active sync.</p>
                      <v-btn
                        color="#7972c0"
                        :to='{
                            name: "IntegrationSelect"
                          }'
                        depressed
                      >
                        <span class="text-white">Add Sync</span>
                      </v-btn>
                    </div>
                    <div v-else>
                      <v-card class="mt-2 py-2 px-4">
                        <p>Synchronized with {{ item.integration.name }}</p>
                        <v-img
                          :lazy-src="$publicPath + item.integration.image"
                          :src="$publicPath + item.integration.image"
                          max-width="150"
                          max-height="130"
                        />
                        <div class="flex mt-4">
                          <v-checkbox
                            v-model="integrationSyncMode"
                            label="Automatic sync every hour"
                            color="#c1c1c1"
                            readonly
                            style="cursor: default"
                          />
                          <v-spacer />
                          <div
                            class="flex align-center"
                            style="max-width: 66px; flex: 1 1 66px"
                          >
                            <v-tooltip
                              top
                              transition="scroll-y-reverse-transition"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <router-link
                                  class='flex align-center justify-center text-decoration-none'
                                  :to='{
                                      name: "IntegrationSyncLog",
                                      params: {
                                        id: item.id
                                      }
                                    }'
                                >
                                  <v-icon
                                    v-bind="attrs"
                                    size="24"
                                    class="mr-1 cursor-pointer"
                                    color="#7671bd"
                                    v-on="on"
                                  >
                                    mdi-history
                                  </v-icon>
                                </router-link>
                              </template>
                              <span>View Sync Logs</span>
                            </v-tooltip>
                            <v-tooltip
                              top
                              transition="scroll-y-reverse-transition"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <router-link
                                  class='flex align-center justify-center'
                                  :to='{
                                      name: "IntegrationSyncContinue",
                                      params: {
                                        system: item.integration.alias,
                                        warehouse: item.id
                                      }
                                    }'
                                >
                                  <img
                                    v-bind="attrs"
                                    class="mr-1 cursor-pointer"
                                    style="height: 16px; width: 16px"
                                    src="@/assets/icons/svg/draw.svg"
                                    title="Edit"
                                    v-on="on"
                                  >
                                </router-link>
                              </template>
                              <span>Edit Sync</span>
                            </v-tooltip>
                            <v-tooltip
                              top
                              transition="scroll-y-reverse-transition"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <img
                                  v-bind="attrs"
                                  style="height: 16px; width: 16px"
                                  src="@/assets/icons/svg/delete1.svg"
                                  class="cursor-pointer"
                                  title="Delete"
                                  v-on="on"
                                  @click="showDeleteConfirmPopup(item)"
                                >
                              </template>
                              <span>Delete Sync</span>
                            </v-tooltip>
                          </div>
                        </div>
                      </v-card>
                    </div>
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs>
          </v-col>
        </v-row>

        <v-row style="max-width: 1400px">
          <v-col>
            <div class="text-left pt-10">
              <v-btn
                depressed
                class="cancel-btn"
                @click="$router.push({name: 'WarehousesList'})"
              >
                Cancel
              </v-btn>
              <v-btn
                depressed
                :disabled="!valid"
                class="save-folder-btn save-btn"
                @click="validate"
              >
                Save
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-form>
    </div>
    <delete-integration
      v-if="toDelete"
      :item="toDelete"
      @doDelete="deleteIntegration"
      @cancel="toDelete = null"
    />
  </div>
</template>

<script>
import service from '@/services/warehouses'
import integrationService from '@/services/integrations'
import deleteIntegration from '@/components/integration/popups/deleteIntegration'
import PageTitle from '@/components/page/PageTitle'
import WarehouseMap from '@/views/warehouses/map'
import { mapGetters } from 'vuex'
import moment from 'moment'
export default {
  name: 'WarehouseEdit',
  components: {
    deleteIntegration,
    PageTitle,
    WarehouseMap
  },
  data: () => ({
    tab: null,
    item: null,
    nameRules: [
      v => !!v.length || 'Warehouse name is required'
    ],
    usersHeaders: [
      {
        text: 'First Name',
        align: 'start',
        sortable: true,
        value: 'firstName'
      },
      {
        text: 'Last Name',
        value: 'lastName',
        sortable: true,
        align: 'start'
      },
      { text: 'Role', value: 'role', sortable: true, align: 'start' },
      { text: 'Email', value: 'email', sortable: true, align: 'start' },
      {
        text: 'Phone Number',
        value: 'phone',
        sortable: true,
        align: 'start'
      },
      { text: 'Created', value: 'createdAt', sortable: true, align: 'start' },
      {
        text: 'Last Active',
        value: 'lastVisit',
        sortable: true,
        align: 'start'
      },
      {
        text: 'Status',
        value: 'inviteStatus',
        sortable: true,
        align: 'start'
      },
      {
        text: '',
        value: 'actions',
        sortable: false,
        align: 'start'
      }
    ],
    usersPerPage: 10,
    usersPage: 1,
    usersPageCount: 0,
    filterUsers: '',
    filterUsersOptiones: [
      'All Users',
      'Confirmed Users',
      'Unconfirmed Users'
    ],
    warehouseName: '',
    valid: true,
    searchUsersInput: '',
    addingUserMenuState: false,
    toDelete: null,
    currentCurrency: 0
  }),
  computed: {
    ...mapGetters('user', ['getDateTimeFormat', 'invitedUsers']),
    ...mapGetters('currency', ['getCurrencyList']),
    currencies() {
      return this.getCurrencyList.map((itm) => {
        return {
          value: itm.id,
          title: `${itm.code} - ${itm.currency} (${this.capitilizeWords(itm.location)})`
        }
      })
    },
    filtredUsersList() {
      const usersFilter = users => {
        this.invitedUsers.forEach(item => {
          if (this.item.info.users.find(user => user.id === item.id)) {
            item.selected = true
          }
        })
        return users.filter(user => {
          return this.$checkEqualityStrings(user.inviteStatus, 'accepted')
        })
      }
      if (this.searchUsersInput) {
        return usersFilter(this.invitedUsers.filter(item => {
          const name = `${item.firstName} ${item.lastName}`
          return name.toLowerCase().search(new RegExp(this.searchUsersInput.toLowerCase()), 'g') >= 0
        }))
      }
      return usersFilter(this.invitedUsers)
    },
    integrationSyncMode: {
      get() {
        return this.$checkEqualityStrings(this.item?.integration?.syncMode, 'AUTO')
      },
      set(v) {
        this.item.integration.syncMode = v ? 'AUTO' : 'MANUAL'
      }
    }
  },
  filters: {
    formatDate: (v, format) => moment(v).format(format)
  },
  beforeRouteEnter(to, from, next) {
    if (!+to.params.id) {
      next(vm => {
        vm.$router.replace({ name: 'WarehousesList' })
      })
    } else {
      next()
    }
  },
  async created() {
    this.$store.dispatch('currency/loadCurrencyList')
    if (+this.$route.params.id) {
      const result = await service.getWarehouse({ id: +this.$route.params.id })
      if (result?.data?.error?.type === 200) {
        this.warehouseName = result.data.data.name
        this.item = result.data.data
        this.currentCurrency = this.item.currencyId || 0
      } else {
        this.$store.dispatch('showSubmitFormErrorPopup', {
          title: 'Error to get warehouse',
          message: result?.data?.error?.message || 'The error has occurred during execution of an operation.'
        })
      }
    }
  },
  methods: {
    capitilizeWords(phrase) {
      return phrase.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    },
    changeCurrency(currencyId) {
      if (!currencyId) {
        return
      }
      this.item.currencyId = currencyId
    },
    async validate() {
      const payload = {
        id: this.item.id,
        ownerId: this.item.ownerId,
        name: this.item.name,
        description: this.item.description,
        currencyId: this.item.currencyId,
        info: {
          users: this.item.info.users.map(item => ({ id: item.id, email: item.email }))
        }
      }
      const result = await service.updateWarehouse(payload)
      if (result?.data?.error?.type === 200) {
        this.$store.dispatch('warehouses/loadWarehousesList')
        this.$router.push({ name: 'WarehousesList' })
      } else {
        this.$store.dispatch('showSubmitFormErrorPopup', {
          title: 'Error to edit warehouse',
          message: result?.data?.error?.message || 'The error has occurred during execution of an operation.'
        })
      }
    },
    deleteUser(id) {
      const users = this.item.info.users
      users.splice(users.findIndex(user => user.id === id), 1)
      this.invitedUsers.find(user => user.id === id).selected = false
    },
    handleChangeSelectedUsersList(item) {
      if (item.selected) {
        delete item.selected
        this.item.info.users.push(item)
      } else {
        this.item.info.users.splice(this.item.info.users.findIndex(user => user.id === item.id), 1)
      }
    },
    showDeleteConfirmPopup(item) {
      this.toDelete = {
        warehouseId: this.item.id,
        warehouseName: this.item.name,
        integrationSystem: this.item.integration.name
      }
    },
    async deleteIntegration() {
      const response = await integrationService.deleteIntegration({ inventoryId: this.toDelete.warehouseId })
      if (response?.data?.error?.type === 200) {
        this.$store.dispatch('integrations/loadIntegrationsList')
        this.toDelete = null
        const result = await service.getWarehouse({ id: +this.$route.params.id })
        if (result?.data?.error?.type === 200) {
          this.warehouseName = result.data.data.name
          this.item = result.data.data
        } else {
          this.$store.dispatch('showSubmitFormErrorPopup', {
            title: 'Error to get warehouse',
            message: result?.data?.error?.message || 'The error has occurred during execution of an operation.'
          })
        }
      }
    }
  }
}
</script>

<style lang="scss">
.v-slide-group__content {
  background-color: rgba(0, 0, 0, 0.05);
}
.v-tabs-slider {
  color: rgb(121, 114, 192) !important;
}
</style>
